"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import { formatFullDateTime, getNextMintStart } from "@/components/meme-calendar/meme-calendar.helpers";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import Image from "next/image";
import Link from "next/link";
import NowMintingStatsItem from "./NowMintingStatsItem";
interface LatestDropNextMintSectionProps {
  readonly drop: ApiDrop;
}
const formatDropTimestamp = (timestamp: number): string | null => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const dateLabel = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric"
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date);
  return `${dateLabel} · ${timeLabel}`;
};
export default function LatestDropNextMintSection(t0) {
  const $ = _c(47);
  const {
    drop
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const media = drop.parts[0]?.media[0];
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  const authorHandle = author.handle ?? author.primary_address;
  const authorName = author.handle ?? "Anonymous";
  let t1;
  if ($[0] !== drop.created_at) {
    t1 = formatDropTimestamp(drop.created_at);
    $[0] = drop.created_at;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const submittedAt = t1;
  const description = drop.metadata.find(_temp2)?.data_value ?? null;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    const nextMintStart = getNextMintStart();
    t2 = formatFullDateTime(nextMintStart, "local");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const nextMintLabel = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="tw-mb-3 tw-block tw-text-xl tw-font-semibold tw-tracking-tight tw-text-iron-100 md:tw-mb-4 md:tw-text-2xl">Next Drop</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== hasTouchScreen || $[5] !== media) {
    t4 = <div className="tw-p-0 lg:tw-col-span-6 xl:tw-col-span-5"><div className="tw-relative tw-flex tw-h-[clamp(360px,65vw,640px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-black/50"><div className="tw-[&>div]:tw-mx-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">{media ? <DropListItemContentMedia media_mime_type={media.mime_type} media_url={media.url} imageObjectPosition="center" imageScale={ImageScale.AUTOx600} disableAutoPlay={hasTouchScreen} disableModal={hasTouchScreen} /> : <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-black/40"><span className="tw-text-sm tw-text-white/40">No image</span></div>}</div></div></div>;
    $[4] = hasTouchScreen;
    $[5] = media;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2"><span className="tw-size-1.5 tw-rounded-full tw-bg-emerald-500" /><span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-5 tw-tracking-wide tw-text-emerald-400">NEXT MINT</span></div>;
    t6 = <span className="tw-mt-1 tw-font-mono tw-text-xs tw-text-white/50">{nextMintLabel}</span>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  const t7 = `/waves?wave=${drop.wave.id}&drop=${drop.id}`;
  let t8;
  if ($[9] !== t7 || $[10] !== title) {
    t8 = <Link href={t7} className="tw-mt-3 tw-text-xl tw-font-semibold tw-leading-tight tw-text-iron-50 tw-no-underline tw-transition-colors tw-duration-300 desktop-hover:hover:tw-text-iron-200 sm:tw-text-2xl md:tw-text-3xl">{title}</Link>;
    $[9] = t7;
    $[10] = title;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== description) {
    t9 = description && <p className="tw-mt-3 tw-line-clamp-2 tw-text-sm tw-leading-relaxed tw-text-iron-300">{description}</p>;
    $[12] = description;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== author.pfp || $[15] !== authorHandle || $[16] !== authorName) {
    t10 = authorHandle ? <Link href={`/${authorHandle}`} className="tw-mt-3 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-no-underline"><ProfileAvatar pfpUrl={author.pfp} alt={authorHandle || authorName} size={ProfileBadgeSize.SMALL} /><span className="tw-min-w-0 tw-truncate tw-text-sm tw-text-iron-200 desktop-hover:hover:tw-text-iron-100">{authorName}</span></Link> : <div className="tw-mt-3 tw-flex tw-min-w-0 tw-items-center tw-gap-2"><ProfileAvatar pfpUrl={author.pfp} alt={authorName} size={ProfileBadgeSize.SMALL} /><span className="tw-min-w-0 tw-truncate tw-text-sm tw-text-iron-200">{authorName}</span></div>;
    $[14] = author.pfp;
    $[15] = authorHandle;
    $[16] = authorName;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t8 || $[20] !== t9) {
    t11 = <div className="tw-flex tw-flex-col">{t5}{t6}{t8}{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = `/waves?wave=${drop.wave.id}`;
  let t13;
  if ($[22] !== drop.wave.name || $[23] !== drop.wave.picture) {
    t13 = drop.wave.picture && <Image src={getScaledImageUri(drop.wave.picture, ImageScale.W_AUTO_H_50)} alt={drop.wave.name} width={20} height={20} className="tw-size-5 tw-shrink-0 tw-rounded-full tw-object-cover tw-ring-1 tw-ring-white/10" />;
    $[22] = drop.wave.name;
    $[23] = drop.wave.picture;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== drop.wave.name) {
    t14 = <span className="tw-min-w-0 tw-break-words">{drop.wave.name}</span>;
    $[25] = drop.wave.name;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t12 || $[28] !== t13 || $[29] !== t14) {
    t15 = <div className="tw-col-span-2"><NowMintingStatsItem label="Wave" allowWrap={true} value={<Link href={t12} className="tw-inline-flex tw-items-center tw-gap-2 tw-text-iron-100 tw-no-underline desktop-hover:hover:tw-text-iron-200">{t13}{t14}</Link>} /></div>;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = submittedAt ?? "\u2014";
  let t17;
  if ($[31] !== t16) {
    t17 = <NowMintingStatsItem label="Submitted" value={t16} />;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== drop.rating) {
    t18 = formatNumberWithCommas(drop.rating);
    $[33] = drop.rating;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = `${t18} TDH`;
  let t20;
  if ($[35] !== t19) {
    t20 = <NowMintingStatsItem label="Rating" value={t19} />;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t15 || $[38] !== t17 || $[39] !== t20) {
    t21 = <div className="tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-pt-4">{t15}{t17}{t20}</div>;
    $[37] = t15;
    $[38] = t17;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t11 || $[42] !== t21) {
    t22 = <div className="tw-p-5 md:tw-p-6 lg:tw-col-span-6 xl:tw-col-span-4"><div className="tw-flex tw-flex-col tw-gap-5">{t11}{t21}</div></div>;
    $[41] = t11;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t22 || $[45] !== t4) {
    t23 = <section className="tw-relative tw-z-50 tw-px-4 tw-pb-4 tw-pt-6 md:tw-px-6 md:tw-pb-8 md:tw-pt-10 lg:tw-px-8">{t3}<div className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-white/[0.03] tw-bg-iron-950"><div className="tw-grid tw-grid-cols-1 tw-items-center tw-gap-x-6 tw-gap-y-6 lg:tw-grid-cols-12 xl:tw-grid-cols-9">{t4}{t22}</div></div></section>;
    $[44] = t22;
    $[45] = t4;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  return t23;
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp(m) {
  return m.data_key === "title";
}